import { Link } from "react-router-dom";
import { Phone, MapPin, Clock, ArrowUpRight } from "lucide-react";
import { Wordmark } from "@/components/Navbar";
import { PHONE_DISPLAY, PHONE_TEL, ADDRESS, BLOG_URL, CITIES } from "@/data/site";

const Footer = () => (
  <footer data-testid="site-footer" className="border-t border-white/10 bg-bear-ink text-white">
    <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:py-20">
      <div className="grid gap-12 md:grid-cols-2 lg:grid-cols-4">
        <div>
          <Link to="/" data-testid="footer-logo" aria-label="Big Bear Heating & Air home">
            <Wordmark />
          </Link>
          <p className="mt-6 max-w-xs text-sm leading-relaxed text-white/60">
            Rugged reliability, modern craft. Central California's comfort
            experts for residential homes, mobile homes, and light
            commercial spaces.
          </p>
          <div className="mt-6 flex items-center gap-2 rounded-full border border-bear-gold/40 px-4 py-2 font-head text-[11px] font-semibold uppercase tracking-[0.14em] text-bear-gold w-fit">
            <span className="pulse-dot h-1.5 w-1.5 rounded-full bg-bear-gold" />
            After-Hours &amp; Weekends Available
          </div>
        </div>

        <div>
          <h3 className="font-head text-xs font-bold uppercase tracking-[0.24em] text-bear-cyan">
            Reach The Bear
          </h3>
          <ul className="mt-6 space-y-4 text-sm">
            <li>
              <a
                href={PHONE_TEL}
                data-testid="footer-call-link"
                className="group flex items-start gap-3 text-white/80 transition-colors hover:text-white"
              >
                <Phone className="mt-0.5 h-4 w-4 shrink-0 text-bear-red" />
                <span>
                  <span className="block font-head text-lg font-bold text-white">{PHONE_DISPLAY}</span>
                  <span className="text-white/50">Click-to-call, after-hours answered</span>
                </span>
              </a>
            </li>
            <li className="flex items-start gap-3 text-white/80">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-bear-cyan" />
              <span data-testid="footer-address">{ADDRESS}</span>
            </li>
            <li className="flex items-start gap-3 text-white/80">
              <Clock className="mt-0.5 h-4 w-4 shrink-0 text-bear-gold" />
              <span>
                <span className="block">Mon – Fri: 7:30 AM – 5:00 PM</span>
                <span className="block text-white/50">After-Hours: 5 – 9 PM (M–F) + Full Weekends</span>
              </span>
            </li>
          </ul>
        </div>

        <div>
          <h3 className="font-head text-xs font-bold uppercase tracking-[0.24em] text-bear-cyan">
            Pages
          </h3>
          <ul className="mt-6 space-y-3 text-sm">
            {[
              { to: "/", label: "Home" },
              { to: "/about", label: "About Us" },
              { to: "/services", label: "Services" },
              { to: "/contact", label: "Contact & Booking" },
            ].map((l) => (
              <li key={l.to}>
                <Link
                  to={l.to}
                  data-testid={`footer-link-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className="text-white/70 transition-colors hover:text-bear-cyan"
                >
                  {l.label}
                </Link>
              </li>
            ))}
            <li>
              <a
                href={BLOG_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-link-blog"
                className="inline-flex items-center gap-1 text-white/70 transition-colors hover:text-bear-cyan"
              >
                Blog <ArrowUpRight className="h-3.5 w-3.5" />
              </a>
            </li>
          </ul>
        </div>

        <div>
          <h3 className="font-head text-xs font-bold uppercase tracking-[0.24em] text-bear-cyan">
            60-Mile Service Radius
          </h3>
          <p className="mt-6 flex flex-wrap gap-x-2 gap-y-1.5 text-sm text-white/70" data-testid="footer-service-area">
            {CITIES.map((c, i) => (
              <span key={c}>
                {c}
                {i < CITIES.length - 1 && <span className="ml-2 text-bear-gold">•</span>}
              </span>
            ))}
          </p>
        </div>
      </div>

      <div className="mt-16 flex flex-col items-center justify-between gap-4 border-t border-white/10 pt-8 sm:flex-row">
        <p className="text-xs text-white/40">
          © {new Date().getFullYear()} Big Bear Heating &amp; Air. All rights reserved.
        </p>
        <p className="font-head text-[11px] font-semibold uppercase tracking-[0.2em] text-white/40">
          Manteca, CA — Central Valley Proud
        </p>
      </div>
    </div>
  </footer>
);

export default Footer;
